import { useId } from 'react'
import { useProducts } from '../context/ProductsContext'

/**
 * Photorealistic SVG amber prescription pill bottle.
 * Same label system as PeptideVial.
 */
export default function PillBottle({
  formulaName = 'BPC-157',
  formulaSubtitle = '',
  concentration = '500 mcg',
  labelSize,
  wrapClassName = 'peptide-vial-float',
  rootClassName = '',
}) {
  const uid = useId().replace(/:/g, '')
  const g = (n) => `${n}-${uid}`
  const { branding } = useProducts()
  const logoSrc = branding?.logoDark?.url ?? '/logos/lookslab-logo.png'

  const W = 120, H = 208

  // Child-proof cap (wide, ridged, white)
  const CAP_X = 14, CAP_W = 92, CAP_Y = 2, CAP_H = 36
  // Cap ridge skirt at bottom
  const SKT_X = 18, SKT_W = 84, SKT_Y = CAP_Y + CAP_H, SKT_H = 6
  // Body (amber cylinder)
  const BDY_X = 20, BDY_W = 80, BDY_Y = SKT_Y + SKT_H, BDY_H = 118
  // Base
  const BSE_Y = BDY_Y + BDY_H, BSE_H = 10

  const MID = W / 2
  const GX1 = BDY_X, GX2 = BDY_X + BDY_W

  // Label
  const LBL_X = BDY_X + 4
  const LBL_W = BDY_W - 8
  const LBL_Y = BDY_Y + 6
  const LBL_H = BDY_H - 20
  const LMX = LBL_X + LBL_W / 2

  const nameDisplay = formulaName.toUpperCase()
  const nameFs = nameDisplay.length <= 5 ? 14
    : nameDisplay.length <= 8 ? 11
    : nameDisplay.length <= 12 ? 9
    : 7
  const useTextLength = nameDisplay.length > 9

  const Z1_H_LOGO = 16
  const Z2_H = nameFs + 10
  const Z3_BADGE_H = 10
  const CONTENT_H = Z2_H + 12 + 8 + 5
  const FREE = LBL_H - Z1_H_LOGO - CONTENT_H - Z3_BADGE_H
  const PAD = Math.max(2, Math.round(FREE / 2))
  const Z1_H = Z1_H_LOGO + PAD
  const Z1_Y = LBL_Y
  const Z2_Y = LBL_Y + Z1_H
  const Z3_Y = Z2_Y + Z2_H
  const Z3_H = LBL_H - Z1_H - Z2_H
  const Z2_TEXT_Y = Z2_Y + 5 + Math.round(nameFs * 0.74)
  const Z3_BOX_TOP = PAD
  const Z3_BOX_H = 12
  const Z3_LYO_Y = Z3_BOX_TOP + Z3_BOX_H + 6
  const Z3_BADGE_TOP = Z3_H - Z3_BADGE_H

  const bsePath = [
    `M ${BDY_X} ${BSE_Y}`,
    `L ${BDY_X + BDY_W} ${BSE_Y}`,
    `L ${BDY_X + BDY_W} ${BSE_Y + BSE_H - 5}`,
    `Q ${BDY_X + BDY_W} ${BSE_Y + BSE_H} ${BDY_X + BDY_W - 6} ${BSE_Y + BSE_H}`,
    `L ${BDY_X + 6} ${BSE_Y + BSE_H}`,
    `Q ${BDY_X} ${BSE_Y + BSE_H} ${BDY_X} ${BSE_Y + BSE_H - 5}`,
    'Z',
  ].join(' ')

  return (
    <div className={rootClassName || undefined}
      style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: '100%' }}>
      <svg
        className={wrapClassName}
        viewBox={`0 0 ${W} ${H}`}
        width={W} height={H}
        style={{ display: 'block', maxWidth: '100%', overflow: 'visible' }}
        aria-label={`${formulaName} ${concentration}`}
        role="img"
      >
        <defs>
          {/* Amber translucent bottle */}
          <linearGradient id={g('amber')} x1={GX1} y1="0" x2={GX2} y2="0" gradientUnits="userSpaceOnUse">
            <stop offset="0%"   stopColor="rgba(90,50,5,0.95)" />
            <stop offset="5%"   stopColor="rgba(130,75,10,0.75)" />
            <stop offset="12%"  stopColor="rgba(190,120,30,0.40)" />
            <stop offset="20%"  stopColor="rgba(220,150,40,0.55)" />
            <stop offset="32%"  stopColor="rgba(200,130,25,0.20)" />
            <stop offset="50%"  stopColor="rgba(195,125,20,0.12)" />
            <stop offset="68%"  stopColor="rgba(200,130,25,0.16)" />
            <stop offset="80%"  stopColor="rgba(210,140,30,0.28)" />
            <stop offset="90%"  stopColor="rgba(150,90,12,0.55)" />
            <stop offset="100%" stopColor="rgba(80,44,4,0.92)" />
          </linearGradient>
          <linearGradient id={g('amberbase')} x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%"   stopColor="#c8780e" />
            <stop offset="100%" stopColor="#8b5208" />
          </linearGradient>
          <radialGradient id={g('amberglow')} cx="38%" cy="42%" r="44%">
            <stop offset="0%"   stopColor="rgba(255,200,80,0.12)" />
            <stop offset="100%" stopColor="rgba(0,0,0,0)" />
          </radialGradient>

          {/* White child-proof cap */}
          <linearGradient id={g('cap')} x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%"   stopColor="#f8f8f8" />
            <stop offset="40%"  stopColor="#e4e4e2" />
            <stop offset="100%" stopColor="#c8c8c6" />
          </linearGradient>
          <linearGradient id={g('capx')} x1="0" y1="0" x2="1" y2="0">
            <stop offset="0%"   stopColor="rgba(0,0,0,0.08)" />
            <stop offset="20%"  stopColor="rgba(255,255,255,0.22)" />
            <stop offset="50%"  stopColor="rgba(255,255,255,0.05)" />
            <stop offset="80%"  stopColor="rgba(255,255,255,0.14)" />
            <stop offset="100%" stopColor="rgba(0,0,0,0.10)" />
          </linearGradient>
          <pattern id={g('capridge')} x="0" y="0" width="3.5" height="1" patternUnits="userSpaceOnUse">
            <rect x="0" y="0" width="1.5" height={CAP_H} fill="rgba(0,0,0,0.06)" />
          </pattern>

          {/* Skirt ring at cap bottom */}
          <linearGradient id={g('skirt')} x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%"   stopColor="#c0c0be" />
            <stop offset="100%" stopColor="#a0a09e" />
          </linearGradient>

          {/* Label 3D wrap — same as vial */}
          <linearGradient id={g('cylshadow')} x1="0" y1="0" x2="1" y2="0">
            <stop offset="0%"   stopColor="rgba(0,0,0,0.78)" />
            <stop offset="8%"   stopColor="rgba(0,0,0,0.52)" />
            <stop offset="16%"  stopColor="rgba(0,0,0,0.20)" />
            <stop offset="26%"  stopColor="rgba(0,0,0,0.04)" />
            <stop offset="36%"  stopColor="rgba(0,0,0,0)" />
            <stop offset="64%"  stopColor="rgba(0,0,0,0)" />
            <stop offset="76%"  stopColor="rgba(0,0,0,0.05)" />
            <stop offset="86%"  stopColor="rgba(0,0,0,0.22)" />
            <stop offset="94%"  stopColor="rgba(0,0,0,0.50)" />
            <stop offset="100%" stopColor="rgba(0,0,0,0.76)" />
          </linearGradient>
          <linearGradient id={g('cylspec')} x1="0" y1="0" x2="1" y2="0">
            <stop offset="0%"   stopColor="rgba(255,255,255,0)" />
            <stop offset="15%"  stopColor="rgba(255,255,255,0.06)" />
            <stop offset="21%"  stopColor="rgba(255,255,255,0.20)" />
            <stop offset="27%"  stopColor="rgba(255,255,255,0.06)" />
            <stop offset="37%"  stopColor="rgba(255,255,255,0)" />
            <stop offset="100%" stopColor="rgba(255,255,255,0)" />
          </linearGradient>
          <radialGradient id={g('lblrad')} cx="42%" cy="50%" r="55%">
            <stop offset="0%"   stopColor="rgba(255,255,255,0.16)" />
            <stop offset="100%" stopColor="rgba(0,0,0,0)" />
          </radialGradient>
          <linearGradient id={g('topedge')} x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%"   stopColor="rgba(0,0,0,0.20)" />
            <stop offset="100%" stopColor="rgba(0,0,0,0)" />
          </linearGradient>
          <linearGradient id={g('botedge')} x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%"   stopColor="rgba(0,0,0,0)" />
            <stop offset="100%" stopColor="rgba(0,0,0,0.18)" />
          </linearGradient>

          <clipPath id={g('lblclip')}>
            <rect x={LBL_X} y={LBL_Y} width={LBL_W} height={LBL_H} rx="2" />
          </clipPath>
          <clipPath id={g('bclip')}>
            <rect x={BDY_X} y={BDY_Y} width={BDY_W} height={BDY_H} />
          </clipPath>

          <radialGradient id={g('shadow')} cx="50%" cy="12%" rx="50%" ry="100%">
            <stop offset="0%"   stopColor="rgba(0,0,0,0.48)" />
            <stop offset="50%"  stopColor="rgba(0,0,0,0.15)" />
            <stop offset="100%" stopColor="rgba(0,0,0,0)" />
          </radialGradient>
        </defs>

        {/* Ground shadow */}
        <ellipse cx={MID} cy={BSE_Y + BSE_H + 8} rx={40} ry={6} fill={`url(#${g('shadow')})`} />

        {/* BASE */}
        <path d={bsePath} fill={`url(#${g('amberbase')})`} />
        <path d={bsePath} fill={`url(#${g('amber')})`} />

        {/* BODY */}
        <rect x={BDY_X} y={BDY_Y} width={BDY_W} height={BDY_H} fill={`url(#${g('amberbase')})`} />
        <rect x={BDY_X} y={BDY_Y} width={BDY_W} height={BDY_H} fill={`url(#${g('amber')})`} />
        <rect x={BDY_X} y={BDY_Y} width={BDY_W} height={BDY_H} fill={`url(#${g('amberglow')})`} />
        <line x1={BDY_X + 0.5} y1={BDY_Y} x2={BDY_X + 0.5} y2={BDY_Y + BDY_H}
          stroke="rgba(220,160,60,0.35)" strokeWidth="0.7" />
        <line x1={BDY_X + BDY_W - 0.5} y1={BDY_Y} x2={BDY_X + BDY_W - 0.5} y2={BDY_Y + BDY_H}
          stroke="rgba(60,30,2,0.55)" strokeWidth="0.7" />

        {/* THREE-ZONE LABEL */}
        <g clipPath={`url(#${g('bclip')})`}>
          <g clipPath={`url(#${g('lblclip')})`}>
            <rect x={LBL_X} y={Z1_Y} width={LBL_W} height={Z1_H} fill="#f5f4f1" />
            <rect x={LBL_X} y={Z1_Y} width={LBL_W} height={Z1_H} fill={`url(#${g('lblrad')})`} />
            <image href={logoSrc}
              x={LBL_X} y={Z1_Y + (Z1_H - Z1_H_LOGO) / 2}
              width={LBL_W} height={Z1_H_LOGO}
              preserveAspectRatio="xMidYMid meet" />

            <rect x={LBL_X} y={Z2_Y} width={LBL_W} height={Z2_H} fill="#111111" />
            <text x={LMX} y={Z2_TEXT_Y} textAnchor="middle"
              fontFamily="'Inter','Helvetica Neue',sans-serif"
              fontSize={nameFs} fontWeight="900" letterSpacing="0.01em" fill="#ffffff"
              {...(useTextLength ? { textLength: LBL_W - 8, lengthAdjust: 'spacingAndGlyphs' } : {})}>
              {nameDisplay}
            </text>

            <rect x={LBL_X} y={Z3_Y} width={LBL_W} height={Z3_H} fill="#f5f4f1" />
            <rect x={LBL_X} y={Z3_Y} width={LBL_W} height={Z3_H} fill={`url(#${g('lblrad')})`} />
            <rect x={LBL_X + 7} y={Z3_Y + Z3_BOX_TOP} width={44} height={Z3_BOX_H}
              fill="none" stroke="#1a1a1a" strokeWidth="1.0" rx="0.5" />
            <text x={LBL_X + 29} y={Z3_Y + Z3_BOX_TOP + Math.round(Z3_BOX_H * 0.72)}
              textAnchor="middle" fontFamily="'Inter','Helvetica Neue',sans-serif"
              fontSize="8.5" fontWeight="800" letterSpacing="0.01em" fill="#111111">
              {(labelSize ?? concentration).toUpperCase()}
            </text>
            <text x={LBL_X + 7} y={Z3_Y + Z3_LYO_Y} textAnchor="start"
              fontFamily="'Inter','Helvetica Neue',sans-serif" fontSize="4.8" fontWeight="600" fill="#555555"
              textLength={44} lengthAdjust="spacingAndGlyphs">ORAL CAPSULES</text>
            <rect x={LBL_X} y={Z3_Y + Z3_BADGE_TOP} width={LBL_W} height={Z3_BADGE_H} fill="#111111" />
            <text x={LMX} y={Z3_Y + Z3_BADGE_TOP + 7.2} textAnchor="middle"
              fontFamily="'Inter','Helvetica Neue',sans-serif"
              fontSize="5" fontWeight="700" letterSpacing="0.08em" fill="#efefef">
              FOR LABORATORY USE ONLY
            </text>

            <rect x={LBL_X} y={LBL_Y} width={LBL_W} height={LBL_H} fill={`url(#${g('cylshadow')})`} />
            <rect x={LBL_X} y={LBL_Y} width={LBL_W} height={LBL_H} fill={`url(#${g('cylspec')})`} />
            <rect x={LBL_X} y={LBL_Y} width={LBL_W} height={8} fill={`url(#${g('topedge')})`} />
            <rect x={LBL_X} y={LBL_Y + LBL_H - 8} width={LBL_W} height={8} fill={`url(#${g('botedge')})`} />
          </g>
        </g>

        {/* CAP SKIRT */}
        <rect x={SKT_X} y={SKT_Y} width={SKT_W} height={SKT_H}
          fill={`url(#${g('skirt')})`} stroke="rgba(80,80,78,0.30)" strokeWidth="0.5" />

        {/* CAP */}
        <rect x={CAP_X} y={CAP_Y} width={CAP_W} height={CAP_H} rx="5"
          fill={`url(#${g('cap')})`} />
        <rect x={CAP_X} y={CAP_Y} width={CAP_W} height={CAP_H} rx="5"
          fill={`url(#${g('capridge')})`} opacity="0.8" />
        <rect x={CAP_X} y={CAP_Y} width={CAP_W} height={CAP_H} rx="5"
          fill={`url(#${g('capx')})`} />
        <rect x={CAP_X + 5} y={CAP_Y + 2} width={CAP_W - 10} height={CAP_H * 0.32} rx="3.5"
          fill="rgba(255,255,255,0.30)" />
        <rect x={CAP_X} y={CAP_Y} width={CAP_W} height={CAP_H} rx="5"
          fill="none" stroke="rgba(80,80,78,0.45)" strokeWidth="0.6" />
        {/* Push-down arrows on cap */}
        <text x={MID} y={CAP_Y + CAP_H * 0.62} textAnchor="middle"
          fontFamily="'Inter','Helvetica Neue',sans-serif"
          fontSize="7" fontWeight="700" letterSpacing="0.10em" fill="rgba(80,80,78,0.55)">
          PUSH ↓ TURN
        </text>

      </svg>
    </div>
  )
}
